import React from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import type { SkillBundleFile } from '../../services/agentSkillsApi'
import type { SkillInfo } from '../../types/agent'
import DisabledReason from '../system/DisabledReason'
import { decodeBase64Text } from '../../utils/skillMarkdown'
import { formatSavedAt, isBayesPriceSkill, isBuiltinSkill, makerOf, NEWER_LABEL, OFF_LABEL, SHADOWED_LABEL } from './SkillsLibrary'

interface SkillDetailDialogProps {
  skill: SkillInfo | null
  /** The skill's stored files; undefined while they load. */
  files: SkillBundleFile[] | undefined
  error: string | null
  /** A skill a person made that this chat has installed. A built-in skill is always present. */
  installed?: boolean
  /** Why Install is disabled (a colleague's skill, a draft), or undefined. */
  installReason?: string
  /** Absent where there is no chat to install into (start page, Admin). */
  onInstall?: (skill: SkillInfo) => void
  onClose: () => void
}

function isBinaryPath(path: string): boolean {
  return /\.(pptx|potx|xlsx|docx|png|jpe?g|gif|webp|pdf|zip|gz|tar|woff2?|ttf|ico)$/i.test(path)
}

/** A skill's SKILL.md and files, read only. The files are fetched by the caller. */
export default function SkillDetailDialog({ skill, files, error, installed, installReason, onInstall, onClose }: SkillDetailDialogProps) {
  if (!skill) return null
  const loading = !files && !error
  // Every chat already has a built-in skill: nothing to install.
  const builtin = isBuiltinSkill(skill)
  const bayesPrice = isBayesPriceSkill(skill)
  const shadowed = bayesPrice ? skill.shadowed_by : ''

  // SKILL.md (the prompt) first, then the rest.
  const sorted = (files ?? []).slice().sort((a, b) => {
    const am = a.path.toUpperCase().endsWith('SKILL.MD') ? 0 : 1
    const bm = b.path.toUpperCase().endsWith('SKILL.MD') ? 0 : 1
    return am - bm || a.path.localeCompare(b.path)
  })

  return (
    <Dialog open={!!skill} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, pr: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">{skill.name}</Typography>
          <Typography variant="body2" color="text.secondary">{skill.description}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close skill details">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          {builtin && <Chip label="Built in" size="small" variant="outlined" />}
          {skill.status === 'draft' && <Chip label="Draft" size="small" color="warning" variant="outlined" />}
          {skill.visibility === 'organizational' && <Chip label="Company-level" size="small" variant="outlined" />}
          {bayesPrice && <Chip label="Bayes Price" size="small" color="success" variant="outlined" />}
          {bayesPrice && skill.off_for_customer && <Chip label={OFF_LABEL} size="small" variant="outlined" />}
          {skill.origin_newer && <Chip label={NEWER_LABEL} size="small" color="info" variant="outlined" />}
          {makerOf(skill) && !builtin && (
            <Typography variant="caption" color="text.secondary">{makerOf(skill)}</Typography>
          )}
        </Box>
        {shadowed && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{SHADOWED_LABEL[shadowed]}.</Typography>}
        {bayesPrice && skill.version_saved_at ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Version saved {formatSavedAt(skill.version_saved_at)}</Typography>
        ) : null}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && <Typography color="error" sx={{ py: 2 }}>{error}</Typography>}

        {!loading && !error && sorted.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2 }}>No files found.</Typography>
        )}

        {!loading && !error && sorted.map((f, i) => {
          const isSkillMd = f.path.toUpperCase().endsWith('SKILL.MD')
          return (
            <Box key={f.path} sx={{ mb: 2 }}>
              {i > 0 && <Divider sx={{ mb: 2 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                <DescriptionOutlinedIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {isSkillMd ? 'SKILL.md (the instructions)' : f.path}
                </Typography>
              </Box>
              {isBinaryPath(f.path) ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Binary file: preview not shown.
                </Typography>
              ) : (
                <Box
                  component="pre"
                  sx={{
                    m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflow: 'auto',
                  }}
                >
                  {decodeBase64Text(f.content)}
                </Box>
              )}
            </Box>
          )
        })}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="info">Close</Button>
        {builtin && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Built in: every chat already has this skill.
          </Typography>
        )}
        {onInstall && !builtin && (installed ? (
          <Chip label="Installed in this chat" color="success" variant="outlined" sx={{ mr: 1 }} />
        ) : (
          <DisabledReason reason={installReason}>
            <Button variant="contained" color="info" onClick={() => onInstall(skill)}>
              Install in this chat
            </Button>
          </DisabledReason>
        ))}
      </DialogActions>
    </Dialog>
  )
}
